import { useRef, useState } from "react";
import Icon from "../Icon/Icon";
import useOnClickOutside from "@/shared/hooks/useOnClickOutside";
import { useTranslation } from "react-i18next";
import Checkbox from "../Checkbox/Checkbox";
import useOrderSort from "@/shared/atoms/useOrderSortAtom";
import useOrderPage from "@/shared/atoms/useOrderPageAtom";
import {
  MAX_ORDER_COUNT_FOR_ARRIVED,
  MAX_ORDER_COUNT_FOR_OTHERS } from
"@/shared/utils/constant";
import { OrderStatus, SortBy } from "@/shared/utils/enum";
import Rotate from "../Table/Rotate";
import useUserSettingsHook from "@/shared/hooks/useUserSettingsHook";
import Tooltip from "@/Components/Tooltip/Tooltip";

const StyledBox = ({ isActive = false, className = "", ml, style = {}, ...props }: any) => (
  <div
    className={`mb-4 flex cursor-pointer items-center rounded-lg border border-border px-[0.6rem] py-2 text-[1.2rem] font-medium hover:bg-grey-secondary ${className}`.trim()}
    style={{
      background: isActive ? "var(--grey-secondary)" : "var(--white)",
      marginLeft: ml,
      ...style,
    }}
    {...props}
  />
);

const StyledButton = ({ className = "", ...props }) => (
  <span
    className={`cursor-pointer rounded-[2rem] bg-primary px-12 py-3 text-center font-semibold text-white hover:opacity-80 ${className}`.trim()}
    {...props}
  />
);

const sorts = [
  { translationKey: "orderTime", value: SortBy.PLACED_DATE_TIME },
  { translationKey: "desiredTime", value: SortBy.HAND_OVER_TIME },
  { translationKey: "zipcode", value: SortBy.ZIP_CODE },
  { translationKey: "source", value: SortBy.SOURCE },
  { translationKey: "status", value: SortBy.STATUS },
];


const OrderCardWrapper = ({ children, title, statues, orderDate }) => {
  const [show, setShow] = useState(false);
  const ref = useRef();
  useOnClickOutside(ref, () => setShow(false));
  const { t } = useTranslation();
  const { showOrderCount } = useUserSettingsHook();
  const orderStatus = statues[0];
  const [sortValues, setSortValues] = useOrderSort();
  const [pageValues, setPageValues] = useOrderPage();
  const sortValueTranslationKey = sorts.find(
    (s) => s.value === sortValues[orderStatus]
  )?.translationKey;

  const totalOrdersCount =
  orderDate?.filter((order) =>
  statues.includes(order?.order_information?.order_status)
  )?.length ?? 0;

  const maxPageCount =
  orderStatus === OrderStatus.ARRIVED ?
  MAX_ORDER_COUNT_FOR_ARRIVED :
  MAX_ORDER_COUNT_FOR_OTHERS;

  const pageCount = (pageValues[orderStatus] + 1) * maxPageCount;
  const hasLoadMoreBtn =
  totalOrdersCount > maxPageCount && pageCount < totalOrdersCount;

  const handleSort = (sort) => {
    setSortValues((prev) => ({
      ...prev,
      [orderStatus]: sortValues[orderStatus] !== sort.value ? sort.value : ""
    }));
  };

  const handlePage = () => {
    setPageValues((prev) => ({
      ...prev,
      [orderStatus]: pageValues[orderStatus] + 1
    }));
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <p className="mb-[2rem]">
          {title}
        </p>
        <div ref={ref} className="relative ml-4 flex">
          <div className="flex w-full justify-between">
            <StyledBox isActive={show} onClick={() => setShow(!show)}>
              <Rotate degrees="180deg" variants="arrow" size="1.2rem" />
              <Rotate degrees="360deg" variants="arrow" size="1.2rem" />
              <span className="mx-[0.5rem]">
                {sortValueTranslationKey ?
                `${t(sortValueTranslationKey)}` :
                "Sort"}
              </span>
              <Icon
                className="h-[1.4rem] w-[1.4rem] fill-text"
                variants="chevron"
                style={{
                  transform: `rotate(${show ? 0 : 180}deg)`,
                  transition: "transform 0.25s ease",
                }}
              />
              
            </StyledBox>
            {showOrderCount &&
            <div className="flex">
                {totalOrdersCount > maxPageCount &&
              <StyledBox ml="0.5rem">{`1 - ${
                pageCount > totalOrdersCount ? totalOrdersCount : pageCount}`
                }</StyledBox>
              }
                <Tooltip content=


              {t("totalOrderCount")}><div><StyledBox ml="0.5rem">
                    
                  {totalOrdersCount}
                </StyledBox></div></Tooltip>
              </div>
            }
          </div>

          {show &&
          <div
            className="absolute right-9 top-[3.75rem] flex w-80 flex-col gap-5 rounded-lg px-5 py-4"
            onClick={(e) => e.stopPropagation()}
            style={{
              border: "1px solid var(--border)",
              height: "auto",
              background: "var(--grey-secondary)",
              zIndex: 999999999,
            }}
          >
            
              {sorts.map((sort) =>
            <Checkbox
              key={sort.value}
              onChange={() => handleSort(sort)}
              sizing="mini"
              active={sortValues[orderStatus] === sort.value}>
              
                  <div onClick={() => handleSort(sort)} className="cursor-pointer">
                    {t(`${sort.translationKey}`)}
                  </div>
                </Checkbox>
            )}
            </div>
          }
        </div>
      </div>

      {children}
      {hasLoadMoreBtn &&
      <div className="mt-4 flex justify-center">
          <StyledButton onClick={handlePage}>Load more</StyledButton>
        </div>
      }
    </div>);

};

export default OrderCardWrapper;